import { defineMessages, type IntlShape } from 'dibs-react-intl';
import { graphql, readInlineData } from 'react-relay';
import { hasKey } from 'dibs-ts-utils/exports/hasKey.ts';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';

import { type getDetailLabelAndValue_item$key } from './__generated__/getDetailLabelAndValue_item.graphql.ts';

const itemFragment = graphql`
    fragment getDetailLabelAndValue_item on Item @inline {
        vertical
        measurement {
            convertedMeasurementsList {
                measurementUnit
                height
                width
                depth
                diameter
                secondaryHeight
            }
        }
        pdpDetailsDisplay {
            detailKey
            detailValues {
                displayName
            }
        }
    }
`;

const dimensionsMessages = defineMessages({
    height: {
        id: 'abf.PdpRespSimilarSoldItemsColumn.dimensions.height',
        defaultMessage: 'H',
    },
    width: {
        id: 'abf.PdpRespSimilarSoldItemsColumn.dimensions.width',
        defaultMessage: 'W',
    },
    depth: {
        id: 'abf.PdpRespSimilarSoldItemsColumn.dimensions.depth',
        defaultMessage: 'D',
    },
    secondaryHeight: {
        id: 'abf.PdpRespSimilarSoldItemsColumn.dimensions.secondaryHeight',
        defaultMessage: 'Seat Height',
    },
    diameter: {
        id: 'abf.PdpRespSimilarSoldItemsColumn.dimensions.diameter',
        defaultMessage: 'Diameter',
    },
});

const fieldLabelsMessages = defineMessages({
    SET_SIZE: {
        id: 'abf.useDetailEntries.setSize',
        defaultMessage: 'Set Size',
    },
    CONDITION: {
        id: 'abf.useDetailEntries.condition',
        defaultMessage: 'Condition',
    },
    DATE_OF_MANUFACTURE: {
        id: 'abf.useDetailEntries.dateOfManufacture',
        defaultMessage: 'Date of Manufacture',
    },
    CREATION_YEAR: {
        id: 'abf.useDetailEntries.creationYear',
        defaultMessage: 'Creation Year',
    },
    PERIOD: {
        id: 'abf.useDetailEntries.period',
        defaultMessage: 'Period',
    },
    DIMENSIONS: {
        id: 'abf.useDetailEntries.dimensions',
        defaultMessage: 'Dimensions ({measurementUnit})',
    },
    WEIGHT: {
        id: 'abf.useDetailEntries.weight',
        defaultMessage: 'Weight',
    },
});

export function getDetailLabelAndValue({
    item: itemRef,
    entry,
    measurementUnit,
    formatMessage,
}: {
    item: getDetailLabelAndValue_item$key;
    entry: string;
    measurementUnit: string;
    formatMessage: IntlShape['formatMessage'];
}): { label: string | null; value: string | null } {
    const item = readInlineData(itemFragment, itemRef);
    const { vertical, measurement, pdpDetailsDisplay } = item;

    if (entry === 'DIMENSIONS') {
        const measurements = measurement?.convertedMeasurementsList?.find(
            m => m?.measurementUnit === measurementUnit
        );

        const { height, width, depth, secondaryHeight, diameter } = measurements || {};

        const filteredDimensions = Object.entries({
            height,
            width,
            depth,
            secondaryHeight,
            diameter,
        })
            .map(([dimensionName, dimensionValue]) => {
                if (!dimensionValue) return null;
                const dimensionMsg = hasKey(dimensionsMessages, dimensionName)
                    ? formatMessage(dimensionsMessages[dimensionName])
                    : null;

                return dimensionMsg ? `${dimensionMsg}: ${dimensionValue}` : null;
            })
            .filter(filterNulls)
            .join(' x ');

        return {
            label: formatMessage(fieldLabelsMessages.DIMENSIONS, {
                measurementUnit: measurementUnit.toLocaleLowerCase(),
            }),
            value: filteredDimensions || null,
        };
    }

    const detail = pdpDetailsDisplay?.find(d => d?.detailKey === entry);
    const labelKey =
        vertical === 'art' && entry === 'DATE_OF_MANUFACTURE' ? 'CREATION_YEAR' : entry;

    return {
        label: hasKey(fieldLabelsMessages, labelKey)
            ? formatMessage(fieldLabelsMessages[labelKey])
            : null,
        value: detail?.detailValues?.[0]?.displayName || null,
    };
}
